import React, { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import { DocumentCopyIcon, EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { getConditionalArray } from 'utils/data';

import {
  ADD_MORTGAGE_HREF,
  ENTITY_TYPE_HREF_MAP,
  getSectionToRedirectByEntityType,
  LOAN_AND_LOCK_SECTION_HREF_MAP,
  PROPERTY_AND_LOAN_ENTITY_TYPES,
  PROPERTY_SECTION_HREF_MAP,
} from '../EditModal/EditModal.constants';

const ActionButton = ({
  anchorEl = null,
  onClearAnchorElement,
  editText,
  entityType,
  section,
  isEditDisabled = false,
}) => {
  const { applicationId, tab } = useParams();
  const history = useHistory();
  const { pathname } = useLocation();

  const onClickEdit = useCallback(
    (_, pathsProps) => {
      onClearAnchorElement();

      const path = LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        subtab: entityType,
        entityId: section ?? getSectionToRedirectByEntityType({ entityType }),
        pathFrom: pathname,
        ...pathsProps,
      });

      history.push(path);
    },
    [applicationId, history, onClearAnchorElement, pathname, tab, entityType, section],
  );

  const onClickSelectProgram = useCallback(
    (_, { section }) => {
      onClearAnchorElement();

      const path = LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        subtab: entityType,
        entityId: section ?? getSectionToRedirectByEntityType({ entityType }),
        pathFrom: pathname,
      });

      history.push(path);
    },
    [onClearAnchorElement, applicationId, tab, entityType, pathname, history],
  );

  // LEN-156: a chattel loan enters property taxes (escrow lines) and homeowners insurance on the
  // standard subject property, edited in Property Info > Property Expenses.
  const onClickEditChattelTaxesAndInsurance = useCallback(() => {
    onClearAnchorElement();

    const path = LoansPaths.applicationManagement.generatePath({
      applicationId,
      tab,
      subtab: ENTITY_TYPE_HREF_MAP.PROPERTY_INFO,
      entityId: PROPERTY_SECTION_HREF_MAP.PROPERTY_EXPENSES,
      pathFrom: pathname,
    });

    history.push(path);
  }, [onClearAnchorElement, applicationId, tab, pathname, history]);

  const items = useMemo(
    () => [
      {
        text: editText,
        onClick: onClickEdit,
        icon: <EditPenIcon size={IconSize[20]} />,
        disabled: isEditDisabled,
      },
      ...getConditionalArray(entityType === ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK && !section, [
        {
          text: 'Select/Edit Program',
          icon: <DocumentCopyIcon size={IconSize[20]} />,
          onClick: e =>
            onClickSelectProgram(e, {
              section: LOAN_AND_LOCK_SECTION_HREF_MAP.PROGRAMS_PRICING,
            }),
        },
      ]),
      ...getConditionalArray(
        entityType === ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK &&
          section === LOAN_AND_LOCK_SECTION_HREF_MAP.ADDITIONAL_FINANCING,
        [
          {
            text: 'Add Additional Mortgage',
            icon: <DocumentCopyIcon size={IconSize[20]} />,
            onClick: e =>
              onClickEdit(e, {
                section: ADD_MORTGAGE_HREF,
              }),
          },
        ],
      ),
      ...getConditionalArray(entityType === ENTITY_TYPE_HREF_MAP.CHATTEL, [
        {
          text: 'Edit Taxes & Insurance',
          icon: <EditPenIcon size={IconSize[20]} />,
          disabled: isEditDisabled,
          onClick: onClickEditChattelTaxesAndInsurance,
        },
      ]),
    ],
    [
      editText,
      onClickEdit,
      entityType,
      section,
      onClickSelectProgram,
      onClickEditChattelTaxesAndInsurance,
      isEditDisabled,
    ],
  );

  return <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />;
};

ActionButton.propTypes = {
  entityType: PropTypes.oneOf(PROPERTY_AND_LOAN_ENTITY_TYPES).isRequired,
  onClearAnchorElement: PropTypes.func.isRequired,
  section: PropTypes.string,
  anchorEl: PropTypes.object,
  editText: PropTypes.string,
  isEditDisabled: PropTypes.bool,
};

export default ActionButton;
